// Web3Login.js
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../supabaseClient';
import { ethers } from 'ethers';
import { useCoin } from '../CoinContext';
import UserProfileModal from './UserProfileModal';
import { fetchUserByWallet, upsertUser, clearUserState } from './userUtils';
import { useMailbox } from '../hooks/useMailbox';

function Web3Login() {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMailboxOpen, setIsMailboxOpen] = useState(false);
  const [selectedMail, setSelectedMail] = useState(null);
  const { walletAddress, setWalletAddress, setCurrentPoints, nickname, setNickname, setDustPoints } = useCoin();

  // Mailbox hook
  const {
    mails,
    unreadCount: unreadMailCount,
    loading: mailLoading,
    fetchMails,
    markAsRead,
    markAllAsRead,
    deleteMail,
    claimReward,
  } = useMailbox(walletAddress);
  const [claimingMail, setClaimingMail] = useState(false);
  const [claimResult, setClaimResult] = useState(null); // { success: bool, message: string, items: [] }

  // useCallback 적용 (Setter는 모두 의존성에 명시)
  const resetUserState = useCallback(() => {
    clearUserState(setWalletAddress, setCurrentPoints, setNickname, setDustPoints);
  }, [setWalletAddress, setCurrentPoints, setNickname, setDustPoints]);

  useEffect(() => {
    const restoreWallet = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        try {
          const userRow = await fetchUserByWallet();
          if (userRow?.wallet_address) setWalletAddress(userRow.wallet_address);
          if (userRow?.current_points != null) setCurrentPoints(userRow.current_points);
          if (userRow?.dust_points != null) setDustPoints(userRow.dust_points);
          if (userRow?.nickname) setNickname(userRow.nickname);
        } catch (err) {
          console.error('Failed to restore wallet:', err);
        }
      }
    };
    restoreWallet();
  }, [setWalletAddress, setCurrentPoints, setDustPoints, setNickname, resetUserState]);

  useEffect(() => {
    if (window.ethereum) {
      const handleDisconnect = () => {
        resetUserState();
        supabase.auth.signOut();
      };

      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) {
          handleDisconnect();
        } else {
          try {
            const normalizedAddress = ethers.getAddress(accounts[0]);
            setWalletAddress(normalizedAddress);
          } catch (err) {
            console.error('Invalid address:', err);
          }
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('disconnect', handleDisconnect);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('disconnect', handleDisconnect);
      };
    }
  }, [setWalletAddress, resetUserState]);

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        resetUserState();
      }
    });
    return () => { listener.subscription?.unsubscribe(); };
  }, [resetUserState]);

  const handleWalletLogin = async () => {
    setErrorMsg('');
    if (!window.ethereum) {
      setErrorMsg("Please install MetaMask!");
      return;
    }
    setLoading(true);

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();

      let normalizedAddress;
      try {
        normalizedAddress = ethers.getAddress(address);
      } catch (err) {
        setErrorMsg("Invalid wallet address");
        setLoading(false);
        return;
      }

      const nonceResponse = await supabase.functions.invoke('get-nonce', {
        body: { address: normalizedAddress }
      });

      console.log('Nonce response:', nonceResponse);

      if (nonceResponse.error) {
        console.error('Nonce error:', nonceResponse.error);
        setErrorMsg("Failed to get authentication nonce");
        setLoading(false);
        return;
      }

      const nonceData = nonceResponse.data;
      if (!nonceData?.nonce) {
        console.error('No nonce in response:', nonceData);
        setErrorMsg("Failed to get authentication nonce");
        setLoading(false);
        return;
      }

      const uri = window.location.origin;
      console.log("uri : "+uri);
      const domain = window.location.hostname;
      const chainId = await signer.provider.getNetwork().then(net => net.chainId);
      const issuedAt = new Date().toISOString();

      const msg = `${domain} wants you to sign in with your Ethereum account:
${address}

Sign in to SSII App

URI: ${uri}
Version: 1
Chain ID: ${chainId}
Nonce: ${nonceData.nonce}
Issued At: ${issuedAt}
Expiration Time: ${nonceData.expiresAt}`;

      const signature = await signer.signMessage(msg);

      const verifyResponse = await supabase.functions.invoke('verify-siwe', {
        body: {
          message: msg,
          signature: signature
        }
      });

      console.log('Verify response:', verifyResponse);

      if (verifyResponse.error) {
        console.error('Verification error:', verifyResponse.error);
        setErrorMsg("Signature verification failed");
        setLoading(false);
        return;
      }

      const verifyData = verifyResponse.data;
      if (!verifyData?.verified) {
        console.error('Not verified:', verifyData);
        setErrorMsg("Signature verification failed");
        setLoading(false);
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.signInWithWeb3({
        chain: 'ethereum',
        message: msg,
        signature,
        statement: "Sign in to SSII App"
      });

      if (authError) {
        console.error('Auth error:', authError);
        setErrorMsg("Authentication failed");
        setLoading(false);
        return;
      }

      if (authData?.user) {
        setWalletAddress(normalizedAddress);

        try {
          const userData = await upsertUser(normalizedAddress);
          if (userData) {
            setCurrentPoints(userData.current_points || 0);
            setNickname(userData.nickname);
          }
        } catch (upsertError) {
          console.error('Upsert error:', upsertError);
          setErrorMsg("Failed to create user profile");
          setLoading(false);
          return;
        }

        window.location.reload();
      }
    } catch (err) {
      console.error('Login error:', err);

      if (err.code === 4001) {
        setErrorMsg("Login cancelled");
      } else if (err.code === -32002) {
        setErrorMsg("Please check your MetaMask");
      } else if (err.message?.includes('user rejected')) {
        setErrorMsg("Login cancelled");
      } else {
        setErrorMsg("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {
          walletAddress ? (
            <>
              <span
                onClick={() => setIsModalOpen(true)}
                style={{
                  fontWeight: 600,
                  color: '#116',
                  fontSize: 13,
                  marginRight: 8,
                  background: '#eef',
                  borderRadius: 12,
                  padding: '4px 12px',
                  wordBreak: 'break-all',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.background = '#dde'}
                onMouseLeave={(e) => e.target.style.background = '#eef'}
              >
                {nickname || `user_${walletAddress.slice(2, 8)}`}
              </span>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => {
                    setIsMailboxOpen(true);
                    fetchMails();
                  }}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    border: 'none',
                    background: '#eef',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#dde'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#eef'}
                  title="Mailbox"
                >
                  ✉️
                </button>
                {unreadMailCount > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      minWidth: 18,
                      height: 18,
                      borderRadius: 9,
                      background: '#e53935',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }}
                  >
                    {unreadMailCount > 99 ? '99+' : unreadMailCount}
                  </div>
                )}
              </div>
            </>
          ) : (
            <button
              onClick={handleWalletLogin}
              style={{
                padding: '6px 16px',
                borderRadius: 20,
                background: '#ffd700',
                border: 'none',
                fontWeight: 600,
                cursor: 'pointer'
              }}
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          )
        }
        {errorMsg && (
          <span style={{ color: '#c00', fontSize: '12px', marginLeft: 12 }}>
            {errorMsg}
          </span>
        )}
      </div>

      <UserProfileModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Mailbox Modal */}
      {isMailboxOpen && ReactDOM.createPortal(
        <div
          onClick={() => setIsMailboxOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 99999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #3AA8B8 0%, #2A8898 100%)',
              borderRadius: 24,
              padding: 24,
              width: '90%',
              maxWidth: 380,
              maxHeight: '80vh',
              overflow: 'auto',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsMailboxOpen(false)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(255,255,255,0.3)',
                color: '#fff',
                fontSize: 18,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ✕
            </button>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 700,
                  color: '#fff',
                }}
              >
                ✉️ Mailbox
              </h2>
              {unreadMailCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 12px',
                    color: '#fff',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Content */}
            <div
              style={{
                background: 'rgba(255,255,255,0.95)',
                borderRadius: 16,
                padding: 16,
                minHeight: 200,
                maxHeight: 400,
                overflowY: 'auto',
              }}
            >
              {mailLoading ? (
                <div style={{ textAlign: 'center', color: '#666', padding: 40 }}>
                  Loading...
                </div>
              ) : mails.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: 40 }}>
                  No messages yet.
                </div>
              ) : selectedMail ? (
                // Mail Detail View
                <div>
                  <button
                    onClick={() => setSelectedMail(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3AA8B8',
                      cursor: 'pointer',
                      fontSize: 14,
                      marginBottom: 12,
                      padding: 0,
                    }}
                  >
                    ← Back to list
                  </button>
                  <div style={{
                    borderBottom: '1px solid #eee',
                    paddingBottom: 12,
                    marginBottom: 12
                  }}>
                    <div style={{
                      fontSize: 10,
                      color: '#999',
                      textTransform: 'uppercase',
                      marginBottom: 4,
                    }}>
                      {selectedMail.mail_type}
                    </div>
                    <h3 style={{ margin: 0, fontSize: 18, color: '#333' }}>
                      {selectedMail.title}
                    </h3>
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                      {new Date(selectedMail.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 14,
                    color: '#444',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {selectedMail.content}
                  </div>

                  {/* Reward Info */}
                  {selectedMail.reward_type && selectedMail.reward_amount && (
                    <div style={{
                      marginTop: 16,
                      padding: 12,
                      background: selectedMail.is_claimed ? '#f5f5f5' : 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
                      borderRadius: 10,
                      border: selectedMail.is_claimed ? '1px solid #ddd' : '2px solid #ffc107',
                    }}>
                      <div style={{
                        fontSize: 12,
                        color: '#666',
                        marginBottom: 4,
                        textTransform: 'uppercase',
                      }}>
                        {selectedMail.is_claimed ? 'Claimed Reward' : 'Reward'}
                      </div>
                      <div style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: selectedMail.is_claimed ? '#999' : '#333',
                      }}>
                        {selectedMail.reward_type === 'pet' && `🐾 ${selectedMail.reward_amount} Pet`}
                        {selectedMail.reward_type === 'points' && `⭐ ${selectedMail.reward_amount} Points`}
                        {selectedMail.reward_type === 'dust' && `✨ ${selectedMail.reward_amount} Dust`}
                      </div>
                      {!selectedMail.is_claimed && (
                        <button
                          onClick={async () => {
                            if (claimingMail) return;
                            setClaimingMail(true);
                            try {
                              const result = await claimReward(selectedMail.id);
                              if (result.success) {
                                setSelectedMail({ ...selectedMail, is_claimed: true });
                                setClaimResult({
                                  success: true,
                                  message: 'Reward claimed successfully!',
                                  items: result.claimedItems || [],
                                  rewardType: result.rewardType,
                                  rewardAmount: result.rewardAmount,
                                });
                              } else {
                                setClaimResult({
                                  success: false,
                                  message: result.error || 'Failed to claim reward',
                                  items: [],
                                });
                              }
                            } catch (err) {
                              setClaimResult({
                                success: false,
                                message: err.message || 'Failed to claim reward',
                                items: [],
                              });
                            } finally {
                              setClaimingMail(false);
                            }
                          }}
                          disabled={claimingMail}
                          style={{
                            marginTop: 10,
                            width: '100%',
                            padding: '10px',
                            border: 'none',
                            borderRadius: 8,
                            background: claimingMail ? '#ccc' : 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                            color: '#fff',
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: claimingMail ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                          }}
                        >
                          {claimingMail ? 'Claiming...' : 'Claim Reward'}
                        </button>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                    <button
                      onClick={async () => {
                        await deleteMail(selectedMail.id);
                        setSelectedMail(null);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: 'none',
                        borderRadius: 8,
                        background: '#e53935',
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ) : (
                // Mail List View
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {mails.map((mail) => (
                    <div
                      key={mail.id}
                      onClick={async () => {
                        if (!mail.is_read) {
                          await markAsRead(mail.id);
                        }
                        setSelectedMail(mail);
                      }}
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: mail.is_read ? '#f5f5f5' : '#fff',
                        border: mail.is_read ? '1px solid #eee' : '2px solid #3AA8B8',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        {!mail.is_read && (
                          <div style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#e53935',
                          }} />
                        )}
                        <span style={{
                          fontSize: 10,
                          color: '#999',
                          textTransform: 'uppercase',
                        }}>
                          {mail.mail_type}
                        </span>
                      </div>
                      <div style={{
                        fontSize: 14,
                        fontWeight: mail.is_read ? 400 : 600,
                        color: '#333',
                        marginBottom: 4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {mail.title}
                      </div>
                      <div style={{
                        fontSize: 11,
                        color: '#999',
                      }}>
                        {new Date(mail.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Claim Result Modal */}
      {claimResult && ReactDOM.createPortal(
        <div
          onClick={() => setClaimResult(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: claimResult.success
                ? 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)'
                : 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
              borderRadius: 20,
              padding: 24,
              width: '90%',
              maxWidth: 340,
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            }}
          >
            {/* Icon */}
            <div style={{
              fontSize: 48,
              marginBottom: 16,
            }}>
              {claimResult.success ? '🎉' : '❌'}
            </div>

            {/* Title */}
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: 20,
              fontWeight: 700,
              color: '#fff',
            }}>
              {claimResult.success ? 'Reward Claimed!' : 'Claim Failed'}
            </h3>

            {/* Message */}
            <p style={{
              margin: '0 0 16px 0',
              fontSize: 14,
              color: 'rgba(255,255,255,0.9)',
              lineHeight: 1.5,
            }}>
              {claimResult.message}
            </p>

            {/* Claimed Items (for pet rewards) */}
            {claimResult.success && claimResult.items && claimResult.items.length > 0 && (
              <div style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 12,
                padding: 12,
                marginBottom: 16,
                maxHeight: 150,
                overflowY: 'auto',
              }}>
                <div style={{
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                }}>
                  You received:
                </div>
                {claimResult.items.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '6px 0',
                      borderBottom: idx < claimResult.items.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                    }}
                  >
                    {item.ssi_image_url && (
                      <img
                        src={item.ssi_image_url}
                        alt={item.ssi_name}
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 8,
                          objectFit: 'cover',
                        }}
                      />
                    )}
                    <div style={{ textAlign: 'left' }}>
                      <div style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#fff',
                      }}>
                        {item.ssi_name || item.type}
                      </div>
                      {item.ssi_grade && (
                        <div style={{
                          fontSize: 11,
                          color: 'rgba(255,255,255,0.7)',
                        }}>
                          {item.ssi_grade}
                        </div>
                      )}
                      {item.amount && (
                        <div style={{
                          fontSize: 11,
                          color: 'rgba(255,255,255,0.7)',
                        }}>
                          +{item.amount}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Points/Dust reward display */}
            {claimResult.success && claimResult.rewardType && claimResult.rewardType !== 'pet' && (
              <div style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 12,
                padding: 12,
                marginBottom: 16,
              }}>
                <div style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: '#fff',
                }}>
                  {claimResult.rewardType === 'points' && `⭐ +${claimResult.rewardAmount} Points`}
                  {claimResult.rewardType === 'dust' && `✨ +${claimResult.rewardAmount} Dust`}
                </div>
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={() => setClaimResult(null)}
              style={{
                width: '100%',
                padding: '12px',
                border: 'none',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default Web3Login;
